/**
 * WinScreen.tsx
 * Win screen
 */

import {
  type CSSProperties,
  type FC,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { createPortal } from 'react-dom';
import { Button } from '@skillarea/lib-frontend';
import { Title, TextBlock } from '@skillarea/lib-frontend';
import { formatMoney } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import styles from '../MatchmakingModal.module.scss';

import winTrophy from './win-trophy.webp';

export interface IWinScreenProps {
  isFreePlay?: boolean;
  onFindNewOpponent: () => void;
  onRematch: () => void;
  payout?: number;
}

const FIREWORK_COLORS = ['#ffcc00', '#b495ff', '#ff3366', '#00ccff', '#33cc33'];
const SHARDS_PER_FIREWORK = 15;
const FIREWORKS_PER_WAVE = [3, 4, 3, 5, 3, 4];

type TFireworkShard = {
  id: string;
  angle: string;
  distance: string;
};

type TFirework = {
  id: string;
  left: string;
  targetY: string;
  launchAngle: string;
  delay: string;
  color: string;
  shards: TFireworkShard[];
};

type TFireworkStyle = CSSProperties & {
  '--left'?: string;
  '--target-y'?: string;
  '--launch-angle'?: string;
  '--delay'?: string;
  '--firework-color'?: string;
  '--angle'?: string;
  '--distance'?: string;
};

const Fireworks = () => {
  const [fireworks] = useState<TFirework[]>(() => {
    const generated: TFirework[] = [];
    let fireworkId = 0;

    FIREWORKS_PER_WAVE.forEach((count, waveIdx) => {
      for (let i = 0; i < count; i++) {
        const leftVal = Math.random() * 100;
        const left = `${leftVal}%`;
        const targetY = `-${70 + Math.random() * 30}vh`;
        const tilt = (50 - leftVal) * 0.2 + (Math.random() - 0.5) * 4;
        const launchAngle = `${tilt}deg`;
        const delay = `${waveIdx * 0.55 + Math.random() * 0.2}s`;
        const color =
          FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];

        const shards = Array.from({ length: SHARDS_PER_FIREWORK }).map(
          (_, j) => {
            const angle = `${(Math.PI * 2 * j) / SHARDS_PER_FIREWORK + (Math.random() - 0.5) * 0.2}rad`;
            const baseDistance = 60 + Math.random() * 60;
            const multiplier = 2.0 + Math.random() * 2.0;
            const distance = `${baseDistance * multiplier}px`;
            return { id: `shard-${fireworkId}-${j}`, angle, distance };
          },
        );

        generated.push({
          id: `fw-${fireworkId}`,
          left,
          targetY,
          launchAngle,
          delay,
          color,
          shards,
        });
        fireworkId++;
      }
    });

    return generated;
  });

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div aria-hidden="true" className={styles.fireworksOverlay}>
      {fireworks.map((fw) => (
        <div
          className={styles.fireworkWrapper}
          key={fw.id}
          style={
            {
              '--left': fw.left,
              '--target-y': fw.targetY,
              '--launch-angle': fw.launchAngle,
              '--delay': fw.delay,
              '--firework-color': fw.color,
            } as TFireworkStyle
          }
        >
          <div className={styles.fireworkExplosion}>
            {fw.shards.map((shard) => (
              <div
                className={styles.fireworkShard}
                key={shard.id}
                style={
                  {
                    '--angle': shard.angle,
                    '--distance': shard.distance,
                    backgroundColor: fw.color,
                    boxShadow: `0 0 6px ${fw.color}`,
                  } as TFireworkStyle
                }
              />
            ))}
          </div>
          <div className={styles.fireworkRocket}>
            <div className={styles.fireworkRocketHead} />
            <div className={styles.fireworkRocketTail} />
          </div>
        </div>
      ))}
    </div>,
    document.body,
  );
};

export const WinScreen: FC<IWinScreenProps> = ({
  isFreePlay = false,
  onFindNewOpponent,
  onRematch,
  payout = 0,
}) => {
  const { t } = useTranslation('matchmaking');
  const [showFireworks, setShowFireworks] = useState(true);
  const payoutLabel = useMemo(() => {
    const formatted = formatMoney(payout, {
      maxDigits: payout % 100 === 0 ? 0 : 2,
    });

    return formatted.replace('$', '').trim();
  }, [payout]);

  useEffect(() => {
    const timer = setTimeout(() => setShowFireworks(false), 5500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={cx(styles.screen, styles.winScreen)}>
      {showFireworks && <Fireworks />}
      <div className={cx(styles.centered, styles.resultScreen)}>
        <div className={styles.resultIcon}>
          <img
            alt={t('alt.winnerTrophy')}
            className={styles.trophyImage}
            src={winTrophy}
          />
        </div>

        <TextBlock className={styles.resultText} color="contrast" size="l">
          {t('result.win.congratulations')}
        </TextBlock>

        <Title className={styles.resultTitle} color="contrast" size="l">
          {t('result.win.title')}
        </Title>

        {payout > 0 && (
          <div className={styles.payoutText}>
            {t('result.win.payout', { amount: payoutLabel })}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.actionButtons}>
          {isFreePlay ? (
            <Button
              className={styles.actionButton}
              color="accent"
              name={t('result.playAgain')}
              onClick={onFindNewOpponent}
              size="s"
              variant="filled"
            >
              {t('result.playAgain')}
            </Button>
          ) : (
            <>
              <Button
                className={styles.actionButton}
                color="accent"
                name={t('result.findNewOpponent')}
                onClick={onFindNewOpponent}
                size="s"
                variant="filled"
              >
                {t('result.findNewOpponent')}
              </Button>
              <Button
                className={styles.actionButton}
                color="lavender"
                name={t('result.rematch')}
                onClick={onRematch}
                size="s"
                variant="outline"
              >
                {t('result.rematch')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
